import { baseURL } from './usermanagement';

/**
 * Centralized profile update helper to ensure consistent profile data management
 * across all components in the application.
 */

/**
 * Update session storage with new profile data
 * @param {Object} newProfileData - New profile data from API
 * @param {Object} additionalFields - Additional fields to update (e.g., avatar_url, profile_photo_url_object)
 */
export const updateSessionStorageProfile = (newProfileData, additionalFields = {}) => {
    try {
        const currentUser = JSON.parse(sessionStorage.getItem("user") || "{}");
        
        // Update with new profile data
        const updatedUser = {
            ...currentUser,
            ...newProfileData,
            ...additionalFields
        };
        
        // Ensure profile_photo_url_object is properly structured
        if (newProfileData.profile_photo_url_object) {
            updatedUser.profile_photo_url_object = {
                ...currentUser.profile_photo_url_object,
                ...newProfileData.profile_photo_url_object
            };
        }
        
        // Update session storage
        sessionStorage.setItem("user", JSON.stringify(updatedUser));
        
        console.log('ProfileUpdateHelper - Session storage updated:', {
            previous: currentUser,
            updated: updatedUser,
            changes: { ...newProfileData, ...additionalFields }
        });
        
        return updatedUser;
    } catch (error) {
        console.error('ProfileUpdateHelper - Error updating session storage:', error);
        return null;
    }
};

/**
 * Refresh profile data from API and update session storage
 * @param {Function} fetchProfileFunction - Function to fetch profile data from API
 * @returns {Promise<Object>} - Updated profile data
 */
export const refreshProfileData = async (fetchProfileFunction) => {
    try {
        const response = await fetchProfileFunction();
        
        if (response && response.status === 200) {
            const profileData = response.data.profile;
            
            // Update session storage with fresh data
            const updatedUser = updateSessionStorageProfile(profileData);
            
            // Dispatch global event to notify all components
            const event = new CustomEvent('profileDataRefreshed', {
                detail: { profileData, updatedUser }
            });
            window.dispatchEvent(event);
            
            console.log('ProfileUpdateHelper - Profile data refreshed:', profileData);
            console.log('ProfileUpdateHelper - Event dispatched: profileDataRefreshed', event);
            return profileData;
        } else {
            console.error('ProfileUpdateHelper - Failed to refresh profile data:', response);
            return null;
        }
    } catch (error) {
        console.error('ProfileUpdateHelper - Error refreshing profile data:', error);
        return null;
    }
};

/**
 * Update profile photo in session storage
 * @param {string} photoType - Type of photo ('avatar', 'profile', 'pet')
 * @param {string} photoValue - New photo value (filename or URL)
 * @param {boolean} isFullUrl - Whether the value is a full URL or just filename
 */
export const updateProfilePhoto = (photoType, photoValue, isFullUrl = false) => {
    try {
        const currentUser = JSON.parse(sessionStorage.getItem("user") || "{}");
        
        let updatedUser = { ...currentUser };
        
        switch (photoType) {
            case 'avatar':
                // For avatar selection, update avatar_url
                if (isFullUrl) {
                    updatedUser.avatar_url = photoValue;
                } else {
                    updatedUser.avatar_url = `${baseURL}/file/avatar/${photoValue}`;
                }
                break;
                
            case 'profile':
                // For profile image upload, update profile_photo_url_object.profile_url
                if (isFullUrl) {
                    updatedUser.profile_picture_url = photoValue;
                } else {
                    // Update the profile_photo_url_object structure
                    if (!updatedUser.profile_photo_url_object) {
                        updatedUser.profile_photo_url_object = {};
                    }
                    updatedUser.profile_photo_url_object.profile_url = photoValue;
                    // Also update profile_picture_url for backward compatibility
                    updatedUser.profile_picture_url = `${baseURL}/file/profile/image/${photoValue}`;
                }
                break;
                
            case 'pet':
                if (isFullUrl) {
                    updatedUser.pet_url = photoValue;
                } else {
                    updatedUser.pet_url = `${baseURL}/file/avatar/${photoValue}`;
                }
                break;
                
            default:
                console.warn('ProfileUpdateHelper - Unknown photo type:', photoType);
                return null;
        }
        
        // Update session storage
        sessionStorage.setItem("user", JSON.stringify(updatedUser));
        
        console.log('ProfileUpdateHelper - Session storage updated for photo type:', photoType, {
            photoValue,
            isFullUrl,
            updatedUser
        });
        
        // Dispatch specific photo update event
        const specificEvent = new CustomEvent(`${photoType}Updated`, {
            detail: { photoType, photoValue, updatedUser }
        });
        window.dispatchEvent(specificEvent);
        
        // Also dispatch general profile update event
        const generalEvent = new CustomEvent('profilePhotoUpdated', {
            detail: { photoType, photoValue, updatedUser }
        });
        window.dispatchEvent(generalEvent);
        
        console.log('ProfileUpdateHelper - Events dispatched:', {
            specific: `${photoType}Updated`,
            general: 'profilePhotoUpdated',
            specificEvent,
            generalEvent
        });
        
        return updatedUser;
    } catch (error) {
        console.error('ProfileUpdateHelper - Error updating profile photo:', error);
        return null;
    }
};

/**
 * Get the best profile photo URL based on priority logic
 * @param {Object} userobj - User object from session storage
 * @returns {string} - Best available profile photo URL
 */
export const getBestProfilePhoto = (userobj) => {
    if (!userobj) return `${baseURL}/file/avatar/1.webp`;
    
    // Priority order:
    // 1. User-uploaded profile photo (profile_photo_url_object.profile_url)
    // 2. OAuth profile photo (profile_photo_url_object.profile_photo_fullurl)
    // 3. Selected avatar (avatar_url)
    // 4. Profile picture (profile_picture_url)
    // 5. Default avatar
    
    if (userobj.profile_photo_url_object?.profile_url) {
        return `${baseURL}/file/avatar/${userobj.profile_photo_url_object.profile_url}`;
    }
    
    if (userobj.profile_photo_url_object?.profile_photo_fullurl) {
        return userobj.profile_photo_url_object.profile_photo_fullurl;
    }
    
    if (userobj.avatar_url) {
        return userobj.avatar_url;
    }
    
    if (userobj.profile_picture_url) {
        return userobj.profile_picture_url;
    }
    
    return `${baseURL}/file/avatar/1.webp`;
};

/**
 * Force refresh all profile-related components
 */
export const forceProfileRefresh = () => {
    console.log('ProfileUpdateHelper - Force profile refresh triggered');
    
    // Dispatch multiple events to ensure all components update
    const events = [
        new CustomEvent('profileDataRefreshed'),
        new CustomEvent('profilePhotoUpdated'),
        new CustomEvent('avatarUpdated')
    ];
    
    events.forEach(event => {
        window.dispatchEvent(event);
        console.log('ProfileUpdateHelper - Event dispatched:', event.type, event);
    });
    
    // Also trigger storage event for components listening to it
    const storageEvent = new StorageEvent('storage', {
        key: 'user',
        newValue: sessionStorage.getItem('user'),
        oldValue: sessionStorage.getItem('user')
    });
    window.dispatchEvent(storageEvent);
    console.log('ProfileUpdateHelper - Storage event dispatched:', storageEvent);
    
    console.log('ProfileUpdateHelper - All refresh events dispatched');
};

/**
 * Subscribe to profile updates with a callback
 * @param {Function} callback - Callback function to execute on profile updates
 * @param {string} eventType - Type of event to listen for ('profileDataRefreshed', 'profilePhotoUpdated', 'avatarUpdated')
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToProfileUpdates = (callback, eventType = 'profileDataRefreshed') => {
    const eventHandler = (event) => {
        console.log(`ProfileUpdateHelper - ${eventType} event received:`, event);
        callback(event.detail || event);
    };
    
    window.addEventListener(eventType, eventHandler);
    console.log(`ProfileUpdateHelper - Subscribed to ${eventType} events`);
    
    // Return unsubscribe function
    return () => {
        window.removeEventListener(eventType, eventHandler);
        console.log(`ProfileUpdateHelper - Unsubscribed from ${eventType} events`);
    };
};

/**
 * Test function to manually trigger profile updates for debugging
 * @param {string} photoType - Type of photo to test ('avatar', 'profile', 'pet')
 * @param {string} testValue - Test value to use
 */
export const testProfileUpdate = (photoType = 'profile', testValue = 'test-photo.webp') => {
    console.log(`ProfileUpdateHelper - Testing profile update for type: ${photoType}, value: ${testValue}`);
    
    try {
        // Update session storage with test data
        const updatedUser = updateProfilePhoto(photoType, testValue, false);
        
        // Force refresh all components
        forceProfileRefresh();
        
        console.log('ProfileUpdateHelper - Test profile update completed:', updatedUser);
        return updatedUser;
    } catch (error) {
        console.error('ProfileUpdateHelper - Error in test profile update:', error);
        return null;
    }
};

/**
 * Get current session storage user data for debugging
 * @returns {Object} - Current user data from session storage
 */
export const getCurrentSessionUser = () => {
    try {
        const userData = sessionStorage.getItem("user");
        return userData ? JSON.parse(userData) : null;
    } catch (error) {
        console.error('ProfileUpdateHelper - Error getting current session user:', error);
        return null;
    }
};

/**
 * Log current profile state for debugging
 */
export const logCurrentProfileState = () => {
    const currentUser = getCurrentSessionUser();
    console.log('ProfileUpdateHelper - Current profile state:', {
        sessionStorage: currentUser,
        profilePhotoObject: currentUser?.profile_photo_url_object,
        avatarUrl: currentUser?.avatar_url,
        profilePictureUrl: currentUser?.profile_picture_url,
        bestPhoto: currentUser ? getBestProfilePhoto(currentUser) : null
    });
};
